// Fonte única de "está no horário de funcionamento?" (antes só existia
// dentro de chat-cta.js). Elementos com `data-business-hours-watch` recebem
// `data-business-hours-status="open"/"closed"` a cada minuto; cada um decide
// como reagir — este componente só publica o estado.
(function () {
  'use strict';

  // Mesma lista de assets/chat-cta.js, duplicada de propósito (estática,
  // poucas linhas) pra não mexer naquele arquivo só por isso.
  const HOLIDAYS = [
    { day: 1, month: 1 }, // Ano Novo
    { day: 21, month: 4 }, // Tiradentes
    { day: 1, month: 5 }, // Dia do Trabalho
    { day: 7, month: 9 }, // Independência do Brasil
    { day: 12, month: 10 }, // Nossa Senhora Aparecida
    { day: 2, month: 11 }, // Finados
    { day: 15, month: 11 }, // Proclamação da República
    { day: 25, month: 12 }, // Natal
  ];

  /** @param {string | undefined} raw "HH:MM" @returns {number} horas decimais */
  function parseTime(raw) {
    const [h = 0, m = 0] = (raw || '00:00').split(':').map(Number);
    return h + m / 60;
  }

  /** @param {Date} now */
  function isHoliday(now) {
    const day = now.getDate();
    const month = now.getMonth() + 1; // getMonth() é 0-indexado
    return HOLIDAYS.some(h => h.day === day && h.month === month);
  }

  const WATCH_SELECTOR = '[data-business-hours-watch]';
  const RECHECK_INTERVAL_MS = 60_000;

  class ZRZSBusinessHours extends HTMLElement {
    constructor() {
      super();
      /** @type {number} */
      this.openHour = 0;
      /** @type {number} */
      this.closeHour = 24;
      /** @type {number[]} */
      this.activeDays = [0, 1, 2, 3, 4, 5, 6];
      /** @type {ReturnType<typeof setInterval> | null} */
      this._interval = null;
      /** @type {MutationObserver | null} */
      this._watchObserver = null;
    }

    connectedCallback() {
      this.openHour = parseTime(this.dataset.openTime);
      this.closeHour = parseTime(this.dataset.closeTime);
      this.activeDays = (this.dataset.activeDays || '0,1,2,3,4,5,6')
        .split(',')
        .filter(s => s !== '')
        .map(Number)
        .filter(n => !isNaN(n) && n >= 0 && n <= 6);

      this._apply();
      this._interval = setInterval(() => this._apply(), RECHECK_INTERVAL_MS);
      this._observeNewWatchers();
    }

    disconnectedCallback() {
      if (this._interval) clearInterval(this._interval);
      if (this._watchObserver) this._watchObserver.disconnect();
    }

    // _apply() só cobre quem já existia na página; um elemento marcado
    // depois (ex.: card injetado ao abrir um fluxo) ficava sem o atributo
    // até o próximo tick de 60s, então aparecia sempre, mesmo fora do
    // horário. Aplica na hora assim que o elemento aparece/é marcado.
    _observeNewWatchers() {
      /** @param {HTMLElement} el */
      const applyTo = el => {
        el.dataset.businessHoursStatus = this.isOpenNow() ? 'open' : 'closed';
      };
      /** @param {Node} node */
      const scan = node => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(WATCH_SELECTOR)) applyTo(node);
        node.querySelectorAll(WATCH_SELECTOR).forEach(applyTo);
      };
      this._watchObserver = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach(scan);
          if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
            applyTo(mutation.target);
          }
        }
      });
      this._watchObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-business-hours-watch'],
      });
    }

    /** @returns {boolean} */
    isOpenNow() {
      const now = new Date();
      if (isHoliday(now)) return false;
      const day = now.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
      const h = now.getHours() + now.getMinutes() / 60;
      return (
        this.activeDays.includes(day) && h >= this.openHour && h < this.closeHour
      );
    }

    _apply() {
      const open = this.isOpenNow();
      const status = open ? 'open' : 'closed';
      this.dataset.status = status;
      document.querySelectorAll(WATCH_SELECTOR).forEach(el => {
        /** @type {HTMLElement} */ (el).dataset.businessHoursStatus = status;
      });
      document.dispatchEvent(
        new CustomEvent('zrzs:business-hours:change', { detail: { open } })
      );
    }
  }

  if (!customElements.get('zrzs-business-hours')) {
    customElements.define('zrzs-business-hours', ZRZSBusinessHours);
  }
})();
